import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import Logo from '../components/ui/Logo';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao processar solicitação');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary p-4 relative overflow-hidden">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10" />

        {/* Background elements */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />

        <div className="w-full max-w-md relative z-10">
          <Card variant="glass" className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-2xl">
            <CardHeader className="space-y-4 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-foreground-primary">
                  Email Enviado!
                </CardTitle>
                <CardDescription className="text-foreground-tertiary mt-2">
                  Se o email estiver cadastrado, você receberá instruções para redefinir sua senha.
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="p-4 text-sm text-foreground-secondary bg-blue-500/10 border border-blue-500/20 rounded-lg backdrop-blur-sm">
                <p className="font-medium mb-2">Próximos passos:</p>
                <ol className="list-decimal list-inside space-y-1 text-foreground-tertiary">
                  <li>Verifique sua caixa de entrada</li>
                  <li>Procure também na pasta de spam</li>
                  <li>Clique no link do email</li>
                  <li>Defina sua nova senha</li>
                </ol>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar para o login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-primary p-4 relative overflow-hidden">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10" />

      {/* Background elements */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />

      <div className="w-full max-w-md relative z-10">
        <Card variant="glass" className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-2xl">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <Logo size={64} />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-foreground-primary">
                Esqueceu a senha?
              </CardTitle>
              <CardDescription className="text-foreground-tertiary mt-2">
                Digite seu email para receber instruções de redefinição
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-4 text-sm text-status-error bg-status-error/10 border border-status-error/20 rounded-lg backdrop-blur-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground-secondary">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Digite seu email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 border-white/30 placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                  />
                </div>
              </div>

              <Button
                type="submit"
                loading={loading}
                loadingText="Enviando..."
                variant="gradient"
                size="lg"
                className="w-full"
              >
                Enviar instruções
              </Button>
            </form>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-foreground-tertiary hover:text-foreground-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para o login
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Decorative elements */}
        <div className="absolute -top-4 -left-4 w-8 h-8 bg-blue-500/20 rounded border border-blue-500/30" />
        <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-purple-500/20 rounded border border-purple-500/30" />
      </div>
    </div>
  );
};

export default ForgotPassword;
